import React from 'react';
import { PropTypes } from 'prop-types';

import { RenderDatePicker } from 'components/Common/Inputs';
import { getFieldType, CROSS_CHECK_DATE_FORMAT } from 'common/Validation/createCustomerValidation';

const MIN_DATE = new Date(1900, 0, 1);
const FIELDS_PER_ROW = 2;

// Two fields to a row, laid out exactly like the Signature Date / Form Type block above: a row of col-6
// halves, each holding a col-4 label and a col-8 input.
const toRows = (fields) => {
  const rows = [];
  for (let i = 0; i < fields.length; i += FIELDS_PER_ROW) {
    rows.push(fields.slice(i, i + FIELDS_PER_ROW));
  }
  return rows;
};

function CrossCheckSection({ fields, values, errors, onChange, jurisdictions, disabled }) {
  if (!fields || fields.length === 0) {
    return null;
  }

  const renderInput = (field) => {
    const id = `cross-check-${ field.key }`;
    const value = values[field.key] ?? '';
    const className = `form-input ${ errors[field.key] ? 'is-error' : '' }`;
    const type = getFieldType(field);

    if (type === 'date') {
      return (
        <RenderDatePicker
          id={ id }
          input={ { value, onChange: newValue => onChange(field.key, newValue || '') } }
          className={ className }
          placeholder="MMM DD, YYYY"
          dateFormat={ CROSS_CHECK_DATE_FORMAT }
          maxDate={ new Date() }
          minDate={ MIN_DATE }
          popperPlacement="bottom-start"
        />
      );
    }

    if (type === 'country') {
      return (
        <select
          id={ id }
          name={ id }
          className={ `form-select ${ errors[field.key] ? 'is-error' : '' }` }
          value={ value }
          disabled={ disabled }
          onChange={ e => onChange(field.key, e.target.value) }
        >
          <option value="">Please select...</option>
          { (jurisdictions || []).map(x => <option key={ x.name } value={ x.name }>{ x.name }</option>) }
        </select>
      );
    }

    return (
      <input
        id={ id }
        name={ id }
        type="text"
        className={ className }
        value={ value }
        maxLength={ field.maxLength > 0 ? field.maxLength : undefined }
        disabled={ disabled }
        autoComplete="off"
        inputMode={ type === 'numeric' ? 'numeric' : undefined }
        onChange={ e => onChange(field.key, type === 'numeric' ? e.target.value.replace(/\D/g, '') : e.target.value) }
      />
    );
  };

  return (
    <div id="cross-check-section" className="cross-check-section">
      <div className="form-group">
        <div className="col-12 col-sm-12">
          <h2><strong>Additional Data</strong></h2>
        </div>
      </div>
      <div className="cross-check-fields">
        { toRows(fields).map(row => (
          <div className="form-group" key={ row[0].key }>
            { row.map(field => (
              <div className="col-6 col-sm-12" key={ field.key }>
                <div className="form-group">
                  <div className="col-4 col-sm-12">
                    <label htmlFor={ `cross-check-${ field.key }` } className="form-label">{ field.label }{ field.mandatory ? ' *' : '' }</label>
                  </div>
                  <div className="col-8 col-sm-12">
                    { renderInput(field) }
                    { errors[field.key] && <p className="form-input-hint is-error">{ errors[field.key] }</p> }
                  </div>
                </div>
              </div>
            )) }
          </div>
        )) }
      </div>
    </div>
  );
}

CrossCheckSection.propTypes = {
  fields: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string,
    label: PropTypes.string,
    mandatory: PropTypes.bool,
    maxLength: PropTypes.number
  })),
  values: PropTypes.shape({}),
  errors: PropTypes.shape({}),
  onChange: PropTypes.func.isRequired,
  jurisdictions: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string })),
  disabled: PropTypes.bool
};

CrossCheckSection.defaultProps = {
  fields: [],
  values: {},
  errors: {},
  jurisdictions: [],
  disabled: false
};

export default CrossCheckSection;
